import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  MatSnackBar,
  MatSnackBarVerticalPosition,
  MatSnackBarHorizontalPosition,
} from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@app/shared/confirm-dialog/confirm-dialog.component';
import { LoadingDialogComponent } from '@app/shared/loading-dialog/loading-dialog.component';
import { InfoDialogComponent } from '@app/shared/info-dialog/info-dialog.component';
import { formatDate } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { ApiResponse } from '@app/models/api-response.model';
import { Setting } from '@app/models/setting.model';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private baseUrl: string = environment.apiBaseUrl;

  private headerRefresh = new Subject<void>();
  headerRefresh$ = this.headerRefresh.asObservable();

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private authService: AuthService
  ) {}

  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  triggerHeaderRefresh(): void {
    this.headerRefresh.next();
  }

  navigateToDefaultUrl(): void {
    const lastUrl = localStorage.getItem('last_url');

    if (lastUrl) {
      this.router.navigateByUrl(lastUrl).catch(() => {
        localStorage.removeItem('last_url');
        this.navigateByPermission();
      });

      return;
    }

    this.navigateByPermission();
  }

  private navigateByPermission(): void {
    if (this.authService.userCanSearchLoads()) {
      this.router.navigate(['/loads/searches']);
      return;
    }

    if (this.authService.userCanSearchTrucks()) {
      this.router.navigate(['/trucks/searches']);
      return;
    }

    this.router.navigate(['/account/connections']);
  }

  // General CRUD Operations
  get(endpoint: string, data: any = {}): Observable<any> {
    const queryString = this.toHttpParams(data).toString();
    return this.http.get(`${this.baseUrl}${endpoint}?${queryString}`);
  }

  post(endpoint: string, data: any = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}${endpoint}`, data);
  }

  index(endpoint: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}`);
  }

  show(endpoint: string, id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}/${id}`);
  }

  put(endpoint: string, id: number, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}${endpoint}/${id}`, data);
  }

  patch(endpoint: string, data: any = {}): Observable<any> {
    return this.http.patch(`${this.baseUrl}${endpoint}`, data);
  }

  delete(endpoint: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}${endpoint}`);
  }

  search(endpoint: string, data: any): Observable<any> {
    const queryString = this.toHttpParams(data).toString();
    return this.http.get(`${this.baseUrl}${endpoint}?${queryString}`);
  }

  // Snackbar
  showSnackBar(
    message: string,
    type: string = 'info',
    duration: number = 3,
    position: MatSnackBarVerticalPosition = 'top'
  ): void {
    const dismiss = 'Close';
    const classs = `${type}-snackbar`;

    this.snackBar.open(message, dismiss, {
      duration: duration * 1000,
      horizontalPosition: 'center' as MatSnackBarHorizontalPosition,
      verticalPosition: position,
      panelClass: [classs],
    });
  }

  successSnackBar(message: string) {
    this.showSnackBar(message, 'success');
  }

  warningSnackBar(message: string) {
    this.showSnackBar(message, 'warning');
  }

  errorSnackBar(message: string) {
    this.showSnackBar(message, 'error');
  }

  infoSnackBar(message: string) {
    this.showSnackBar(message, 'info');
  }

  // Dialogs
  confirm(question: string): Observable<string> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: { question },
    });

    return dialogRef.afterClosed();
  }

  openInfoDialog(message: string): void {
    this.dialog.open(InfoDialogComponent, {
      data: { message },
    });
  }

  openLoadingDialog(message: string = 'Please wait...'): void {
    this.dialog.open(LoadingDialogComponent, {
      disableClose: true,
      data: { message },
    });
  }

  closeLoadingDialog(): void {
    this.dialog.closeAll();
  }

  handleError(error: any): void {
    const errorMessage = error.error?.message ?? 'Unknown error';

    if (errorMessage !== 'Unauthenticated.') {
      this.showSnackBar(errorMessage, 'error');
    }
  }

  // FMCSA
  getCarrierByDot(dotNumber: number): Observable<any> {
    return this.http.get(`${this.baseUrl}fmcsa-carriers/dot`, {
      params: { dot_number: dotNumber },
    });
  }

  getCarrierByMC(mcNumber: string): Observable<any> {
    return this.http.get(`${this.baseUrl}fmcsa-carriers/mc`, {
      params: { mc_number: mcNumber },
    });
  }

  // Locations
  getLocations(location: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}locations/search`, {
      params: { location },
    });
  }

  // Map
  getDistance(origin: string, destination: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}map/distance`, {
      params: { origin, destination },
    });
  }

  getDrivingDistance(origin: string, destination: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}map/driving-distance`, {
      params: { origin, destination },
    });
  }

  getAirDistance(origin: string, destination: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}map/air-distance`, {
      params: { origin, destination },
    });
  }

  // Loads
  getMyLoads(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}loads`);
  }

  getMyLoad(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}loads/${id}`);
  }

  refreshLoad(id: number): Observable<any> {
    return this.http.patch(`${this.baseUrl}loads/${id}/refresh`, {});
  }

  toggleVisibility(id: number): Observable<any> {
    return this.http.patch(`${this.baseUrl}loads/${id}/visibility`, {});
  }

  // Trucks
  getMyTrucks(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}trucks`);
  }

  getMyTruck(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}trucks/${id}`);
  }

  refreshTruck(id: number): Observable<any> {
    return this.http.patch(`${this.baseUrl}trucks/${id}/refresh`, {});
  }

  toggleTruckVisibility(id: number): Observable<any> {
    return this.http.patch(`${this.baseUrl}trucks/${id}/visibility`, {});
  }

  // Searches
  getMyLoadSearches(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}loads/searches`);
  }

  getMyTruckSearches(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}trucks/searches`);
  }

  // Search Templates
  getMyLoadSearchTemplates(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}loads/search-templates`);
  }

  getMyTruckSearchTemplates(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}trucks/search-templates`);
  }

  // Templates
  getMyLoadTemplates(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}loads/templates`);
  }

  getMyTruckTemplates(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}trucks/templates`);
  }

  // Saved / Favorites
  getSavedLoads(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}saved/loads`);
  }

  getSavedTrucks(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}saved/trucks`);
  }

  toggleSavedLoad(loadId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}saved/loads/toggle`, {
      load_id: loadId,
    });
  }

  toggleSavedTruck(truckId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}saved/trucks/toggle`, {
      truck_id: truckId,
    });
  }

  clearLoadFavs(): void {
    localStorage.removeItem('loadFavs');
  }

  clearTruckFavs(): void {
    localStorage.removeItem('truckFavs');
  }

  // Settings
  getSystemGlobalSettings(): Observable<ApiResponse<Setting>> {
    return this.http.get<ApiResponse<Setting>>(`${this.baseUrl}settings`);
  }

  // Legal
  getDocument(
    documentName: 'privacy-policy' | 'terms-of-service'
  ): Observable<any> {
    return this.http.get(`${this.baseUrl}legal/${documentName}`);
  }

  private toHttpParams(data: any): HttpParams {
    let params = new HttpParams();

    Object.entries(data || {}).forEach(([key, raw]) => {
      let val: any = raw;

      if (
        val === null ||
        val === undefined ||
        val === '' ||
        val === 'null' ||
        val === 'undefined'
      ) {
        return;
      }

      if (val instanceof Date) {
        val = formatDate(val, 'yyyy-MM-dd', 'en-US');
      }

      if (Array.isArray(val)) {
        val
          .filter((v) => v !== null && v !== undefined && v !== '')
          .forEach((v) => (params = params.append(`${key}[]`, String(v))));
      } else if (typeof val === 'object') {
        params = params.set(key, JSON.stringify(val));
      } else {
        params = params.set(key, String(val));
      }
    });

    return params;
  }
}